import { useEffect, useState } from 'react';
import type { Audit, AuditDetailsInput, AuditOutcome } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { OUTCOME_OPTIONS } from '../audits/auditStatus';
import { useCompanyUsers } from '../audits/useCompanyUsers';

interface EditAuditDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  audit: Audit;
  /** Resolves null on success, or the reason it failed (shown in the dialog). */
  onSave: (payload: AuditDetailsInput) => Promise<string | null>;
}

/** A completed audit: an approver corrects name, location, outcome and summary (date and lead stay). */
function isCompleted(audit: Audit): boolean {
  return audit.status === 'completed' || audit.status === 'expired';
}

/**
 * In progress: name, date, location, lead auditor. Completed (approvers): name,
 * location, outcome and summary, without reopening; the change goes on the trail.
 */
export default function EditAuditDetailsDialog({ open, onOpenChange, audit, onSave }: EditAuditDetailsDialogProps) {
  const { options, loading, error: usersError, retry } = useCompanyUsers(open ? audit.customer : null);
  const [name, setName] = useState<string>('');
  const [auditDate, setAuditDate] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [lead, setLead] = useState<string>('');
  const [outcome, setOutcome] = useState<AuditOutcome>('');
  const [summary, setSummary] = useState<string>('');
  const completed = isCompleted(audit);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(audit.name ?? '');
    setAuditDate(audit.audit_date?.slice(0, 10) ?? '');
    setLocation(audit.location ?? '');
    setLead(String(audit.lead_auditor ?? ''));
    setOutcome(audit.outcome ?? '');
    setSummary(audit.summary ?? '');
    setError(null);
  }, [open, audit]);

  const valid = name.trim().length <= 200 && (completed ? outcome !== '' : auditDate !== '' && lead !== '');

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);
    const failure = await onSave(completed
      ? { name: name.trim(), location: location.trim(), outcome, summary: summary.trim() }
      : {
        name: name.trim(), audit_date: auditDate, location: location.trim(),
        // Only a changed lead is sent: one who has since left must not block a rename.
        ...(Number(lead) !== audit.lead_auditor ? { lead_auditor: Number(lead) } : {}),
      });
    setSaving(false);
    if (failure) setError(failure);
    else onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit audit details</DialogTitle>
          <DialogDescription>
            {audit.supplier_name} · {audit.template_name}
            {completed && ' — corrections are recorded on the follow-up trail. Answers, date and validity stay as completed.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-audit-name">Audit name <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <Input id="edit-audit-name" className="h-12 text-base" maxLength={200} value={name}
              onChange={(e) => setName(e.target.value)} placeholder="e.g. Pergola seat – factory 2" />
            <p className="text-sm text-muted-foreground">Shown in the audit list instead of the template name</p>
          </div>
          {completed ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-audit-location">Location</Label>
                <Input id="edit-audit-location" className="h-12 text-base" maxLength={255} value={location}
                  onChange={(e) => setLocation(e.target.value)} placeholder="Factory / site visited" />
              </div>
              <div className="space-y-2">
                <Label>Outcome *</Label>
                <Select value={outcome} onValueChange={(v) => setOutcome(v as AuditOutcome)}>
                  <SelectTrigger className="h-12 text-base" aria-label="Outcome"><SelectValue placeholder="Choose the outcome" /></SelectTrigger>
                  <SelectContent>
                    {OUTCOME_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="py-3 text-base">{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-audit-summary">Summary</Label>
                <Textarea id="edit-audit-summary" rows={3} className="text-base" value={summary}
                  onChange={(e) => setSummary(e.target.value)} />
              </div>
            </>
          ) : (
          <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-audit-date">Audit date *</Label>
              <Input id="edit-audit-date" type="date" className="h-12 text-base" value={auditDate}
                onChange={(e) => setAuditDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-audit-location">Location</Label>
              <Input id="edit-audit-location" className="h-12 text-base" maxLength={255} value={location}
                onChange={(e) => setLocation(e.target.value)} placeholder="Factory / site visited" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-audit-lead">Lead auditor *</Label>
            <SearchableSelect id="edit-audit-lead" options={options} value={lead} onChange={setLead} size="lg"
              placeholder={loading ? 'Loading users…' : 'Choose the lead auditor'} disabled={loading} />
            {usersError && (
              <p className="flex items-center gap-2 text-sm text-destructive">
                Could not load your team&apos;s users.
                <Button type="button" variant="link" className="h-auto p-0" onClick={retry}>Retry</Button>
              </p>
            )}
          </div>
          </>
          )}
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button size="lg" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button size="lg" onClick={submit} disabled={!valid || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save details
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
